import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';

// Context
import { AuthProvider } from './context/AuthContext';
import { GameProvider } from './context/GameContext';
import { LayerProvider } from './context/LayerContext';
import { ContainerProvider } from './context/ContainerContext';
import { HistoryProvider } from './context/HistoryContext';

// Layout Components
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';

// Pages
import LoginPage from './pages/LoginPage';
import MapEditorPage from './pages/MapEditorPage';
import ProfilePage from './pages/ProfilePage';
import GalleryPage from './pages/GalleryPage';
import FeedbackPage from './pages/FeedbackPage';

// Protection
import ProtectedRoute from './components/ProtectedRoute';

// Styles
import './App.css';

// Layout wrapper component to conditionally render header/footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isMapEditorPage = location.pathname === '/app' || location.pathname === '/' || location.pathname === '/map-editor/new';
  
  return (
    <div className="app">
      {!isMapEditorPage && <Header />}
      <main className={`main-content ${isMapEditorPage ? 'no-padding' : ''}`}>
        {children}
      </main>
      {!isMapEditorPage && <Footer />}
    </div>
  );
};

// Component that provides the correct mapId to LayerProvider
const LayerProviderWrapper = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Determine mapId based on current route and search params
  let mapId = 'default';
  
  if (location.pathname === '/app' || location.pathname === '/' || location.pathname === '/map-editor/new') {
    const gameId = searchParams.get('gameId');
    const sharedGameId = searchParams.get('sharedGameId');
    // If we're on /map-editor/new, always use 'new' as mapId
    if (location.pathname === '/map-editor/new') {
      mapId = 'new';
    } else {
      mapId = gameId || sharedGameId || 'new';
    }
  }
  
  return (
    <LayerProvider mapId={mapId}>
      {children}
    </LayerProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <GameProvider>
        <HistoryProvider>
          <Router>
            <LayerProviderWrapper>
              <ContainerProvider>
                    <Routes>
                      {/* Public Routes */}
                  <Route path="/login" element={
                    <AppLayout>
                      <LoginPage />
                    </AppLayout>
                  } />

                      {/* Protected Routes */}
                      <Route path="/app" element={
                        <ProtectedRoute>
                      <AppLayout>
                          <MapEditorPage />
                      </AppLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/map-editor/new" element={
                        <ProtectedRoute>
                      <AppLayout>
                          <MapEditorPage />
                      </AppLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/profile" element={
                        <ProtectedRoute>
                      <AppLayout>
                          <ProfilePage />
                      </AppLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/gallery" element={
                        <ProtectedRoute>
                      <AppLayout>
                          <GalleryPage />
                      </AppLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/feedback" element={
                        <ProtectedRoute>
                      <AppLayout>
                          <FeedbackPage />
                      </AppLayout>
                        </ProtectedRoute>
                      } />

                      {/* Redirects */}
                      <Route path="/" element={<Navigate to="/app" replace />} />
                      <Route path="*" element={<Navigate to="/app" replace />} />
                    </Routes>
              </ContainerProvider>
            </LayerProviderWrapper>
          </Router>
        </HistoryProvider>
      </GameProvider>
    </AuthProvider>
  );
}

export default App;
